import React, { PropsWithChildren, useCallback, useEffect, useState } from 'react';
import { Box, BoxProps, Container, ContainerProps } from '@mui/material';
import { ControlsStringAssets, MessagesStringAssets, PageAndViewTitleStringAssets } from '../../../../assets/stringAssets';
import { enumAlertType } from '../../../enums';
import { IDocumentLink } from '../../../../dataObjects/models/digitalMedia/DocumentLink';
import { typeUniqueId } from '../../../../dataObjects/types';
import { IStoreState } from '../../../../uiMiddleware-redux/store/IStoreState';
import { enumBeaconType, enumCollectionViewType, enumWorkflowState } from '../../../../dataObjects/enums';
import TwoButtonAcceptanceDialog from '../../../dialogs/TwoButtonAcceptanceDialog/TwoButtonAcceptanceDialog';
import { Beacon } from '../../../../dataObjects/models/alerts/Beacon';
import { useReduxWorkflowState } from '../../../customHooks';
import ActivityIndicatorDialog from '../../../dialogs/ActivityIndicatorDialog/ActivityIndicatorDialog';
import { IActivityIndicatorData } from '../../../dialogs/ActivityIndicatorData';
import { AlertInfo, IAlertInfo } from '../../../../dataObjects/models/alerts/AlertInfo';
import { useGetCollectionViewType } from '../../../customHooks/useGetCollectionViewType';
import GenericToggleViewContainer from '../../GenericToggleViewContainer/GenericToggleViewContainer';
import { DocumentLinksCardGridView } from '../DocumentLinksCardGridView/DocumentLinksCardGridView';
import DocumentLinksListView from '../DocumentLinksListView/DocumentLinksListView';
import { styled } from '@mui/styles';
import { IDocumentLinkViewModel } from '../../../../dataObjects/viewModels/documentLinkViewModel';
import { useAppDispatch } from '../../../../uiMiddleware-redux/store/configureStore';
import { documentLinkDeleteRequest, documentLinkDeleteStatusChange } from '../../../../uiMiddleware-redux/slices/documentLink/documentLinkDeleteStatusSlice';
import { beaconChange } from '../../../../uiMiddleware-redux/slices/beacon/beaconSlice';
import { alertInfoChange } from '../../../../uiMiddleware-redux/slices/alertInfo/alertInfoSlice';


/*** Using the Material UI Emotion Styling library, declare 'styled' instances for each area/object. 
 *** NOTE: These must be declared outside of the React Functional Component to ensure that the styled 
 *** objects will be properly rendered within the DOM. 
 ***/

// a styled Box (equivalent to a <div>), providing an area padding at the top of the view
const StyledBoxForPaddingAtopView = styled((props: BoxProps) => (
  <Box
    {...props}
  />
))(({ theme }) => ({
  marginTop: theme.spacing(2),
}));

// a styled Container for the View Container
const StyledContainerForViewContainer = styled((props: ContainerProps) => (
  <Container
    {...props}
  />
))(({ theme }) => ({
  flexGrow: 1,
  paddingLeft: 0,
  paddingRight: 0,
}));

export interface IDocumentLinksViewProps extends PropsWithChildren<unknown> {
  parentId?: typeUniqueId // the parent DigitalMedia for the DocumentLinks
  documentLinksToDisplay: Array<IDocumentLinkViewModel>;
  allowAddingNewItems: boolean;
  documentLinksDataLoading: boolean;
}

export const DocumentLinksView: React.FC<IDocumentLinksViewProps> = (props: IDocumentLinksViewProps) => {

  DocumentLinksView.displayName = 'Document Links View';

  // whether to display console logs (displayConsoleLogs && console.log statements)
  const displayConsoleLogs: boolean = false;

  const { parentId, documentLinksToDisplay, allowAddingNewItems, documentLinksDataLoading } = props;

  // call custom hook to get the current view type, so the value can be used to initialize the View Type state variable
  const collectionViewType: enumCollectionViewType = useGetCollectionViewType();

  // the DocumentLinkViewModels associcated with the current Digital Media
  // const [documentLinks, setDocumentLinks] = useState<Array<IDocumentLink>>(documentLinksToDisplay);
  const [documentLinkViewModels, setDocumentLinkViewModels] = useState<Array<IDocumentLinkViewModel>>(documentLinksToDisplay);
  // whether data is currently loading
  const [dataLoading, setDataLoading] = useState<boolean>(documentLinksDataLoading);
  // if user is deleting a DocumentLink, the DocumentLink to delete
  const [documentLinkToDelete, setDocumentLinkToDelete] = useState<IDocumentLink | undefined>(undefined);
  // whether to show the Delete Confirmation dialog
  const [showDeleteConfirmationDlg, setShowDeleteConfirmationDlg] = useState<boolean>(false);

  // for dispatching redux actions
  const dispatch = useAppDispatch();

  /**
   * @function resetDeleteDocumentLinkStatus Resets the Delete status in Redux state
   */
  const resetDeleteDocumentLinkStatus: () => void = useCallback(() => {
    dispatch(documentLinkDeleteStatusChange(null));
  }, [dispatch])

  // use a custom hook to evaluate the redux state for the deleteDocumentLink workflow and any deleteDocumentLink error
  const { workflowState: deleteDocumentLinkStatus, errorState: deleteDocumentLinkFailure } = useReduxWorkflowState((state: IStoreState) => state.deleteDocumentLinkStatus, (state: IStoreState) => state.deleteDocumentLinkFailure);

  useEffect(() => {
    if (documentLinkViewModels !== documentLinksToDisplay) {
      setDocumentLinkViewModels(documentLinksToDisplay);
    }
  }, [documentLinksToDisplay, documentLinkViewModels]);

  useEffect(() => {
    if (dataLoading !== documentLinksDataLoading) {
      setDataLoading(documentLinksDataLoading);
    }
  }, [documentLinksDataLoading, dataLoading]);

  /**
   * @method handleDocumentLinkDelete Handles requests to delete a Channel
   * @param documentLink The DocumentLink to be deleted
   */
  async function handleDocumentLinkDelete(documentLink: IDocumentLink): Promise<void> {
    // set the documentLink to be deleted into local state
    setDocumentLinkToDelete(documentLink);

    // set flag to show the delete confirmation dialog
    setShowDeleteConfirmationDlg(true);
  }

  function handleDocumentLinkDeleteConfirmed(): void {
    // using the documentLink object to be deleted from the local state, dispatch an action to delete the documentLink object
    if (documentLinkToDelete !== undefined) {
      dispatch(documentLinkDeleteRequest(documentLinkToDelete));
    }

    // call method to reset the documentLink delete local state details
    documentLinkDeleteReset();
  }

  function handleDocumentLinkDeleteCanceled(): void {
    // call method to reset the documentLink delete local state details
    documentLinkDeleteReset();
  }

  function documentLinkDeleteReset() {
    // reset the documentLink to be deleted to 'undefined' in local state, since we're done with the object
    setDocumentLinkToDelete(undefined);

    // set flag to hide the delete confirmation dialog
    setShowDeleteConfirmationDlg(false);
  }


  // data to indicate whether an activity indicator should be displayed and, if so, the message
  let activityIndicatorData: IActivityIndicatorData = {
    showActivityIndicator: false,
    activityIndicatorMessage: ''
  }

  // data to indicate whether an alert should be displayed
  let alertInfo: IAlertInfo | undefined = undefined;

  // indicates whether the object has been successfully deleted
  const [successfullyDeletedObject, setSuccessfullyDeletedObject] = useState<boolean>(false);

  // define an effect that will process whenever the successfullyCompletedPage state changes
  useEffect(() => {
    if (successfullyDeletedObject) {
      // dispatch an action to reset the deleteTopicStatus
      resetDeleteDocumentLinkStatus();

      // notify user via a Beacon notification that the documentLink has been deleted
      dispatch(beaconChange(new Beacon(undefined, enumBeaconType.Success, MessagesStringAssets.documentLink_DeleteConfirmationHeader, MessagesStringAssets.documentLink_DeleteSuccess)));

      // reset the successfullyDeletedObject flag
      setSuccessfullyDeletedObject(false);
    }
  }, [successfullyDeletedObject, dispatch, resetDeleteDocumentLinkStatus]);

  if (dataLoading) {
    // display an activity indicator
    activityIndicatorData.showActivityIndicator = true;
    activityIndicatorData.activityIndicatorMessage = MessagesStringAssets.documentLinks_FetchingRequested;
  } else {
    // if the workflow is in the midst of performing deletion of a documentLink, prepare an alert message fragment to display progress message(s)
    if (deleteDocumentLinkStatus !== undefined && deleteDocumentLinkStatus !== null) {
      if (deleteDocumentLinkStatus === enumWorkflowState.Requested || deleteDocumentLinkStatus === enumWorkflowState.InProgress) {
        // display an activity indicator
        activityIndicatorData.showActivityIndicator = true;
        activityIndicatorData.activityIndicatorMessage = MessagesStringAssets.documentLink_DeleteRequested;
      } else if (deleteDocumentLinkStatus === enumWorkflowState.Success) {
        // if the successfullyDeletedObject is curently in a 'false' state, we'll want the flag to indicate that the deletion was a success
        if (!successfullyDeletedObject) {
          // set flag to indicate that the object has been successfully deleted, which will force a re-render to allow for cleanup and user notification
          setSuccessfullyDeletedObject(true);
        }
      } else if (deleteDocumentLinkStatus === enumWorkflowState.Failure) {
        // display an alert message
        // alertFragment = generateJsxAlertMessageFragment(MessagesStringAssets.documentLink_DeleteFailure, enumJsxAlertMessageDisplayVariants.Failure, false, deleteDocumentLinkFailureMessage, MessagesStringAssets.substitutionKeyword);
        let failureMessage = 'Unknown error';
        if (deleteDocumentLinkFailure && deleteDocumentLinkFailure instanceof Error) {
          failureMessage = deleteDocumentLinkFailure.message;
        }
        alertInfo = new AlertInfo(true, enumAlertType.Error, failureMessage);
        dispatch(alertInfoChange(alertInfo));
      }
    }
  }

  // Establish the text to be displayed on the '+ New' button
  let addNewButtonText: string = '';
  if (allowAddingNewItems) {
    addNewButtonText = ControlsStringAssets.documentNew;
  }


  return (
    <>
      <StyledBoxForPaddingAtopView />
      <GenericToggleViewContainer
        viewTitle={PageAndViewTitleStringAssets.viewTitle_Documents}
        addNewButtonText={addNewButtonText}
        urlForAddNewButtonNavigation={`/${parentId}/documentLink`}
      >

        {/* Container for embedded data view */}
        <StyledContainerForViewContainer>

          {/* If there are DocumentLinks to display and the current view selection is CardsGridView, display the DocumentLinksCardGridView */}
          {documentLinkViewModels &&
            (collectionViewType === enumCollectionViewType.CardsGridView) &&
            <DocumentLinksCardGridView documentLinkViewModels={documentLinkViewModels} onDelete={handleDocumentLinkDelete} />
          }

          {/* If there are DocumentLinks to display and the current view selection is ListView, display the DocumentLinksListView */}
          {documentLinkViewModels &&
            (collectionViewType === enumCollectionViewType.ListView) &&
            <DocumentLinksListView documentLinkViewModels={documentLinkViewModels} onDelete={handleDocumentLinkDelete} />
          }
        </StyledContainerForViewContainer>

        {/* Delete DocumentLink Confirmation Dialog */}
        <TwoButtonAcceptanceDialog
          showDialog={showDeleteConfirmationDlg}
          headerText={MessagesStringAssets.documentLink_DeleteConfirmationHeader}
          bodyText={MessagesStringAssets.documentLink_DeleteConfirmation}
          acceptanceButtonText={ControlsStringAssets.confirmButtonText}
          nonAcceptanceButtonText={ControlsStringAssets.cancelButtonText}
          onAcceptance={handleDocumentLinkDeleteConfirmed}
          onNonAcceptance={handleDocumentLinkDeleteCanceled}
        />

        {activityIndicatorData.showActivityIndicator &&
          <ActivityIndicatorDialog activityLabel={activityIndicatorData.activityIndicatorMessage} />
        }

      </GenericToggleViewContainer>
    </>
  );
}

export default DocumentLinksView;
